import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Dices, Compass } from "lucide-react";
import { getRandomTheme, getLostGames, getUserTier } from "@/services/api";
import { useToast } from "@/hooks/useToast";
import { ThemeSelector } from "./ThemeSelector";
import { cn } from "@/lib/utils/general";
import { debugLayout } from "@/utils/debug";
import { GameGenesisInput } from "./GameGenesisInput";
import { ThemeDetails } from "./ThemeDetails";
import { ThemeCardTypes } from "./ThemeCardTypes";
import { useUser } from "@clerk/clerk-react";
import { ThemeCreationProgress } from "./ThemeCreationProgress";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useNavigate } from "react-router-dom";
import { Theme } from "@/types/themes";

const hasAccess = (tier: string): boolean => {
  const access = tier === 'admin' || tier === 'starter';
  console.log('Checking access for tier:', tier, 'Result:', access);
  return access;
};

export function GameGenesis() {
  const { user } = useUser();
  const [ideaText, setIdeaText] = useState("");
  const [showError, setShowError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [themes, setThemes] = useState<Theme[]>([]);
  const [hasResponse, setHasResponse] = useState(false);
  const { toast } = useToast();
  const containerRef = useRef<HTMLDivElement>(null);
  const [selectedTheme, setSelectedTheme] = useState<Theme | null>(null);
  const [step, setStep] = useState<'input' | 'themes' | 'details' | 'cardTypes' | 'progress'>('input');
  const [isLoadingLost, setIsLoadingLost] = useState(false);
  const [userTier, setUserTier] = useState<string>('starter');
  const navigate = useNavigate();

  useEffect(() => {
    if (containerRef.current && process.env.NODE_ENV === 'development') {
      debugLayout(containerRef.current);
    }
  }, []);

  useEffect(() => {
    const checkTier = async () => {
      console.log('Checking user tier...');
      const tier = await getUserTier();
      console.log('Retrieved tier:', tier);
      setUserTier(tier);
    };
    checkTier();
  }, []);

  const handleRandomTheme = async () => {
    if (!hasAccess(userTier)) {
      toast({
        variant: "destructive",
        title: "Feature not available",
        description: "Please upgrade to Starter tier to use this feature",
      });
      return;
    }
    setIsLoading(true);
    try {
      const response = await getRandomTheme({
        userId: user?.id?.toString()
      });
      
      if (response.success) {
        console.log('Random theme response:', response.data);
        setHasResponse(true);
        // Parse the response data if it's a string
        const titles = typeof response.data === 'string' 
          ? JSON.parse(response.data) 
          : response.data;
        setThemes(titles);
        setStep('themes');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to get random theme. Please try again."
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFindLostGames = async () => {
    if (!hasAccess(userTier)) {
      toast({
        variant: "destructive",
        title: "Feature not available",
        description: "Please upgrade to Starter tier to use this feature",
      });
      return;
    }
    setIsLoadingLost(true);
    try {
      const response = await getLostGames();
      if (response.success) {
        setHasResponse(true);
        const titles = typeof response.data === 'string' 
          ? JSON.parse(response.data) 
          : response.data;
        setThemes(titles);
        setStep('themes');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to find lost games. Please try again."
      });
    } finally {
      setIsLoadingLost(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (!hasAccess(userTier)) {
        toast({
          variant: "destructive",
          title: "Feature not available",
          description: "Please upgrade to Starter tier to use this feature",
        });
        return;
      }
      const trimmedText = ideaText.trim();
      if (trimmedText.length < 7 && trimmedText.length > 0) {
        setShowError(true);
      }
    }
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setIdeaText(e.target.value);
    setShowError(false);
  };

  const handleThemeGenerated = (generatedThemes: Theme[]) => {
    setHasResponse(true);
    setThemes(generatedThemes);
    setStep('themes');
  };
  
  const handleSendToShelter = () => {
    setStep('input');
    setSelectedTheme(null);
    setHasResponse(false);
    toast({
      title: "Theme Saved",
      description: "Your theme has been sent to the Shelter for others to discover.",
    });
  };

  const handleCreateDeck = () => {
    setStep('progress');
  };

  return (
    <div className="w-full flex flex-col gap-4">
      {userTier === 'free' && (
        <Alert className="bg-muted">
          <AlertDescription className="flex items-center justify-between">
            <span>Upgrade to Starter tier to create custom game themes</span>
            <Button 
              variant="default"
              onClick={() => navigate('/pricing')}
              className="ml-4"
            >
              Upgrade Now
            </Button>
          </AlertDescription>
        </Alert>
      )}
      <div className="rounded-md border bg-card w-full border-none">
        <div className="flex flex-col justify-between min-h-[calc(100dvh-8rem)]">
          {/* Top section */}
          <div className={cn(
            "w-full flex items-center justify-center transition-all duration-300 p-4",
            hasResponse ? "hidden" : ""
          )}>
            <div className="text-center space-y-6 w-full max-w-xl mx-auto">
              <h1 className="text-4xl text-game-title font-instrument mt-40 mb-5">
                Game on, let's build!
              </h1>
              <div className="flex items-center justify-center gap-4">
                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={handleRandomTheme}
                  disabled={isLoading || isLoadingLost}
                  className="rounded-2xl border-border hover:bg-secondary 
                           font-normal bg-game-input-bg text-muted-foreground hover:text-game-accent 
                           shadow-md text-sm px-4 py-1"
                >
                  {isLoading ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-game-accent border-t-transparent" />
                      <span>Generating...</span>
                    </div>
                  ) : (
                    <>
                      <Dices className="h-5 w-5 mr-2 text-blue-pill" />
                      Get Fresh Ideas
                    </>
                  )}
                </Button>

                <Button 
                  variant="outline" 
                  size="lg" 
                  onClick={handleFindLostGames}
                  disabled={isLoading || isLoadingLost}
                  className="rounded-2xl border-border hover:bg-secondary 
                           font-normal bg-game-input-bg text-muted-foreground hover:text-theme-one
                           shadow-md text-sm px-4 py-1"
                >
                  {isLoadingLost ? (
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-theme-one border-t-transparent" />
                      <span>Searching...</span>
                    </div>
                  ) : (
                    <>
                      <Compass className="h-5 w-5 mr-2 text-red-pill" />
                      Find Lost Games
                    </>
                  )}
                </Button>
              </div>
            </div>
          </div>

          {/* Middle section */}
          <div className={cn(
            "w-full overflow-y-auto transition-all duration-300 p-4 flex-1",
            !hasResponse ? "hidden" : ""
          )}>
            {step === 'themes' && (
              <ThemeSelector 
                themes={themes}
                onSelect={(theme) => {
                  setSelectedTheme(theme);
                  setStep('details');
                }}
                isLoading={isLoading}
              />
            )}
            {step === 'details' && selectedTheme && (
              <ThemeDetails
                themeId={selectedTheme.id}
                name={selectedTheme.name}
                theme={selectedTheme}
                onBack={() => {
                  setSelectedTheme(null);
                  setStep('themes');
                }}
                onContinue={() => setStep('cardTypes')}
              />
            )}
            {step === 'cardTypes' && selectedTheme && (
              <ThemeCardTypes
                themeId={selectedTheme.id}
                onBack={() => setStep('details')}
                onContinue={handleCreateDeck}
                onSendToShelter={handleSendToShelter}
              />
            )}
            {step === 'progress' && selectedTheme && (
              <ThemeCreationProgress
                themeId={selectedTheme.id}
                artStyleId="default"
              />
            )}
          </div>

          {/* Bottom section */}
          {!hasResponse && (
            <div className="mt-auto">
              <GameGenesisInput
                value={ideaText}
                onChange={handleTextChange}
                onKeyDown={handleKeyPress}
                onThemeGenerated={handleThemeGenerated}
                showError={showError}
                disabled={userTier === 'free'}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}